import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Collapse,
  Paper,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Modal,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminCourseTree } from "../../../redux/Actions/adminCourseTreeAction";
import {
  fetchAdminApprovedCourses,
  clearAdminApprovedCourses,
} from "../../../redux/Actions/adminApprovedCoursesAction";
import {
  fetchStudentCourseRequests,
  clearStudentCourseRequests,
} from "../../../redux/Actions/studentCourseRequestAction";
import { updateStudentRequest } from "../../../redux/Actions/studentRequestUpdateAction";
import MessageCard from "../../MessageCard/MessageCard";
import AdminNavbar from "./AdminNavbar";
import api from "lib/api/api";

const StudentCourseRequestsPage = () => {
  const dispatch = useDispatch();
  const { treeData } = useSelector((state) => state.adminCourseTree);
  const { approvedCourses, loading, error } = useSelector(
    (state) => state.adminApprovedCourses
  );
  const studentRequestState = useSelector(
    (state) => state.studentCourseRequests || {}
  );

  const [expandedParentIndex, setExpandedParentIndex] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isPayed, setIsPayed] = useState(false);
  const [paymentStartedAt, setPaymentStartedAt] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("");
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const updateState = useSelector((state) => state.studentRequestUpdate);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  useEffect(() => {
    dispatch(fetchAdminCourseTree());
  }, [dispatch]);

  const handleParentToggle = (index) => {
    const node = treeData[index];
    const isSameParent = expandedParentIndex === index;

    setExpandedParentIndex(isSameParent ? null : index);
    setExpandedRequest(null);
    setTeacherFilter("");
    setSelectedRequestIds([]);

    dispatch(clearStudentCourseRequests());
    dispatch(clearAdminApprovedCourses());

    if (isSameParent) {
      setSelectedNode(null);
      return;
    }

    setSelectedNode({ ...node, type: "single" });
    dispatch(fetchAdminApprovedCourses(node.id, "single"));
  };

  const handleSelectChild = (child) => {
    setExpandedRequest(null);
    setTeacherFilter("");
    setSelectedRequestIds([]);
    setSelectedNode({ ...child, type: "single" });

    dispatch(clearStudentCourseRequests());
    dispatch(clearAdminApprovedCourses());
    dispatch(fetchAdminApprovedCourses(child.id, "single"));
  };

  const handleToggleRequestCollapse = (teacherCourseId) => {
    const isExpanding = expandedRequest !== teacherCourseId;
    setExpandedRequest(isExpanding ? teacherCourseId : null);
    if (isExpanding) {
      dispatch(fetchStudentCourseRequests(teacherCourseId));
    }
  };

  const openEditModal = (request) => {
    setSelectedRequest(request);
    setIsPayed(request.payed);
    setPaymentStartedAt(
      request.paymentStartedAt ? request.paymentStartedAt.split("T")[0] : ""
    );
    setEditModalOpen(true);
  };

  const closeModal = () => {
    setEditModalOpen(false);
    setSelectedRequest(null);
  };

  const handleApproveRequest = () => {
    if (!selectedRequest || !selectedRequest.teacherCourseId) return;

    dispatch(
      updateStudentRequest(
        selectedRequest.requestId,
        selectedRequest.studentId,
        selectedRequest.teacherCourseId,
        () => {
          setMessage("Confirmation email sent and student enrolled successfully.");
          setMessageType("success");
          setEditModalOpen(false);
          dispatch(fetchStudentCourseRequests(selectedRequest.teacherCourseId));
        }
      )
    );
  };

  const toggleRequestSelection = (requestId) => {
    setSelectedRequestIds((prev) =>
      prev.includes(requestId)
        ? prev.filter((id) => id !== requestId)
        : [...prev, requestId]
    );
  };

  const toggleAllRequests = (requests) => {
    const ids = requests.map((req) => req.requestId);
    const allSelected = ids.every((id) => selectedRequestIds.includes(id));
    setSelectedRequestIds((prev) =>
      allSelected
        ? prev.filter((id) => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  const deleteSelectedRequests = async (teacherCourseId) => {
    if (!selectedRequestIds.length) return;
    if (!window.confirm(`Delete ${selectedRequestIds.length} selected request(s)? Paid/approved requests will be blocked.`)) {
      return;
    }
    setDeleteLoading(true);
    setMessage(null);
    try {
      await api.post("/admin/requests/delete-bulk", selectedRequestIds);
      setMessage("Selected requests deleted successfully.");
      setMessageType("success");
      setSelectedRequestIds([]);
      dispatch(fetchStudentCourseRequests(teacherCourseId));
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not delete selected requests.");
      setMessageType("error");
      dispatch(fetchStudentCourseRequests(teacherCourseId));
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteSingleRequest = async (request) => {
    if (!window.confirm(`Delete request #${request.requestId}? Paid/approved requests will be blocked.`)) {
      return;
    }
    setDeleteLoading(true);
    setMessage(null);
    try {
      await api.delete(`/admin/requests/${request.requestId}`);
      setMessage("Request deleted successfully.");
      setMessageType("success");
      setSelectedRequestIds((prev) => prev.filter((id) => id !== request.requestId));
      dispatch(fetchStudentCourseRequests(request.teacherCourseId));
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not delete request.");
      setMessageType("error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const deriveTeacherName = (course) => {
    const first = (course.teacherFirstName || "").trim();
    const last = (course.teacherLastName || "").trim();
    const fullName = `${first} ${last}`.trim();
    return fullName || "Unknown Teacher";
  };

  const deriveTeacherKey = (course) => {
    if (course.teacherId) {
      return `teacher-${course.teacherId}`;
    }

    const first = (course.teacherFirstName || "").trim().toLowerCase();
    const last = (course.teacherLastName || "").trim().toLowerCase();
    const combined = `${first}|${last}`;

    if (combined !== "|") {
      return combined;
    }

    return `course-${course.teacherCourseId}`;
  };

  const isCompleteView = selectedNode?.type === "complete";

  const groupedCourses = useMemo(() => {
    if (!Array.isArray(approvedCourses) || approvedCourses.length === 0) {
      return [];
    }

    if (!isCompleteView) {
      return approvedCourses.map((course) => ({
        teacherKey: deriveTeacherKey(course),
        teacherName: deriveTeacherName(course),
        courses: [course],
      }));
    }

    const groupedMap = new Map();

    approvedCourses.forEach((course) => {
      const key = deriveTeacherKey(course);
      const teacherName = deriveTeacherName(course);

      if (teacherFilter && key !== teacherFilter) {
        return;
      }

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          teacherKey: key,
          teacherName,
          courses: [],
        });
      }

      groupedMap.get(key).courses.push(course);
    });

    return Array.from(groupedMap.values());
  }, [approvedCourses, isCompleteView, teacherFilter]);

  const teacherOptions = useMemo(() => {
    if (!isCompleteView || !Array.isArray(approvedCourses)) {
      return [];
    }

    const optionMap = new Map();
    approvedCourses.forEach((course) => {
      const key = deriveTeacherKey(course);
      if (!optionMap.has(key)) {
        optionMap.set(key, deriveTeacherName(course));
      }
    });

    return Array.from(optionMap.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [approvedCourses, isCompleteView]);

  return (
    <div>
      <AdminNavbar />
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box display="flex" gap={4}>
          <Box width="25%" sx={{ minWidth: 200 }}>
            <Typography variant="h6" gutterBottom>
              Course Tree
            </Typography>
            {treeData.map((node, index) => (
              <Box key={index} mb={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleParentToggle(index)}
                >
                  {node.title}
                </Button>
                <Collapse in={expandedParentIndex === index}>
                  <Box ml={2}>
                    {node.children?.map((child, cIndex) => (
                      <Button
                        key={cIndex}
                        fullWidth
                        variant="text"
                        onClick={() => handleSelectChild(child)}
                        sx={{ textAlign: "left" }}
                      >
                        - {child.title}
                      </Button>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>

          <Box flexGrow={1}>
            <Typography variant="h4" gutterBottom>
              Student Course Requests
            </Typography>
            <Paper sx={{ p: 3 }}>
              {selectedNode && (
                <>
                  <Typography variant="h6">
                    Approved Courses for {selectedNode.title}:
                  </Typography>

                  {loading && (
                    <Typography>Loading approved courses...</Typography>
                  )}
                  {error && <Typography color="error">{error}</Typography>}

                  {false && isCompleteView && teacherOptions.length > 0 && (
                    <FormControl fullWidth sx={{ my: 2 }}>
                      <InputLabel id="teacher-filter-label">
                        Filter by teacher
                      </InputLabel>
                      <Select
                        labelId="teacher-filter-label"
                        value={teacherFilter}
                        label="Filter by teacher"
                        onChange={(e) => setTeacherFilter(e.target.value)}
                      >
                        <MenuItem value="">All teachers</MenuItem>
                        {teacherOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {groupedCourses.length > 0
                    ? groupedCourses.map(({ teacherKey, teacherName, courses }, groupIndex) => (
                        <Box
                          key={teacherKey || groupIndex}
                          mt={isCompleteView ? 3 : 2}
                        >
                          {isCompleteView && (
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              sx={{ mb: 1 }}
                            >
                              {teacherName}
                            </Typography>
                          )}
                          {courses.map((course) => {
                            const isExpanded =
                              expandedRequest === course.teacherCourseId;
                            const requestState =
                              studentRequestState[course.teacherCourseId] || {};
                            const {
                              loading: requestLoading,
                              requests = [],
                              error: requestError,
                            } = requestState;

                            return (
                              <Box
                                key={course.teacherCourseId}
                                mt={2}
                                p={2}
                                border="1px solid #ddd"
                                borderRadius={2}
                              >
                                <Typography>
                                  <strong>Teacher:</strong> {course.teacherFirstName} {course.teacherLastName}
                                </Typography>
                                <Typography>
                                  <strong>Start:</strong> {new Date(course.startDate).toLocaleDateString()}
                                </Typography>
                                <Typography>
                                  <strong>End:</strong> {new Date(course.endDate).toLocaleDateString()}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="h6">
                                  Requested Students
                                </Typography>
                                <Button
                                  onClick={() =>
                                    handleToggleRequestCollapse(
                                      course.teacherCourseId
                                    )
                                  }
                                  sx={{ mb: 2 }}
                                >
                                  {isExpanded
                                    ? "Hide Requested Students"
                                    : "Show Requested Students"}
                                </Button>

                                <Collapse in={isExpanded}>
                                  <Box
                                    sx={{ maxHeight: 300, overflowY: "auto" }}
                                  >
                                    {requestLoading && (
                                      <Typography>Loading requests...</Typography>
                                    )}
                                    {requestError && (
                                      <Typography color="error">
                                        {requestError}
                                      </Typography>
                                    )}
                                    {!requestLoading && requests.length === 0 && (
                                      <Typography>No requests found.</Typography>
                                    )}
                                    {!requestLoading && requests.length > 0 && (
                                      <>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                          <Typography variant="body2" color="text.secondary">
                                            {selectedRequestIds.length} selected
                                          </Typography>
                                          <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            disabled={!selectedRequestIds.length || deleteLoading}
                                            onClick={() => deleteSelectedRequests(course.teacherCourseId)}
                                          >
                                            Delete selected
                                          </Button>
                                        </Box>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell padding="checkbox">
                                                <Checkbox
                                                  size="small"
                                                  checked={requests.length > 0 && requests.every((req) => selectedRequestIds.includes(req.requestId))}
                                                  indeterminate={requests.some((req) => selectedRequestIds.includes(req.requestId)) && !requests.every((req) => selectedRequestIds.includes(req.requestId))}
                                                  onChange={() => toggleAllRequests(requests)}
                                                />
                                              </TableCell>
                                              <TableCell>Name</TableCell>
                                              <TableCell>Email</TableCell>
                                              <TableCell>Status</TableCell>
                                              <TableCell>Payment</TableCell>
                                              <TableCell>Email Request</TableCell>
                                              <TableCell>Email Confirmed</TableCell>
                                              <TableCell>Coupon Used</TableCell>
                                              <TableCell>Requested At</TableCell>
                                              <TableCell>Action</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {requests.map((req) => (
                                              <TableRow key={req.requestId} selected={selectedRequestIds.includes(req.requestId)}>
                                                <TableCell padding="checkbox">
                                                  <Checkbox
                                                    size="small"
                                                    checked={selectedRequestIds.includes(req.requestId)}
                                                    onChange={() => toggleRequestSelection(req.requestId)}
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  {req.studentFirstName} {req.studentLastName}
                                                </TableCell>
                                                <TableCell>{req.studentEmail}</TableCell>
                                                <TableCell>{req.status}</TableCell>
                                                <TableCell>
                                                  <Box
                                                    sx={{
                                                      display: "inline-block",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: 2,
                                                      backgroundColor: req.payed
                                                        ? "green"
                                                        : "red",
                                                      color: "white",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    {req.payed ? "Yes" : "No"}
                                                  </Box>
                                                </TableCell>
                                                <TableCell>
                                                  <Box
                                                    sx={{
                                                      display: "inline-block",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: 2,
                                                      backgroundColor: req.emailRequestSent
                                                        ? "green"
                                                        : "red",
                                                      color: "white",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    {req.emailRequestSent ? "Sent" : "No"}
                                                  </Box>
                                                </TableCell>
                                                <TableCell>
                                                  <Box
                                                    sx={{
                                                      display: "inline-block",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: 2,
                                                      backgroundColor: req.emailConfirmationSent
                                                        ? "green"
                                                        : "red",
                                                      color: "white",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    {req.emailConfirmationSent ? "Sent" : "No"}
                                                  </Box>
                                                </TableCell>
                                                <TableCell>
                                                  <Box
                                                    sx={{
                                                      display: "inline-block",
                                                      px: 1.5,
                                                      py: 0.5,
                                                      borderRadius: 2,
                                                      backgroundColor: req.usedCoupon
                                                        ? "success.dark"
                                                        : "grey",
                                                      color: "white",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    {req.usedCoupon ? "Yes" : "No"}
                                                  </Box>
                                                </TableCell>
                                                <TableCell>
                                                  {new Date(req.requestDate).toLocaleString()}
                                                </TableCell>
                                                <TableCell>
                                                  <Box sx={{ display: "flex", gap: 1 }}>
                                                    <Button
                                                      variant="outlined"
                                                      onClick={() => openEditModal(req)}
                                                    >
                                                      Edit
                                                    </Button>
                                                    <Button
                                                      variant="outlined"
                                                      color="error"
                                                      disabled={deleteLoading}
                                                      onClick={() => deleteSingleRequest(req)}
                                                    >
                                                      Delete
                                                    </Button>
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </>
                                    )}
                                  </Box>
                                </Collapse>
                              </Box>
                            );
                          })}
                        </Box>
                      ))
                    : !loading && (
                        <Typography>
                          {isCompleteView && teacherFilter
                            ? "No teachers match the current filter."
                            : "No approved courses found for this course."}
                        </Typography>
                      )}
                </>
              )}
            </Paper>
          </Box>
        </Box>
      </Container>

      <Modal open={editModalOpen} onClose={closeModal}>
        <Box
          sx={{
            backgroundColor: "white",
            p: 4,
            width: "50%",
            mx: "auto",
            my: "10%",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6">Edit Student Request</Typography>
          {selectedRequest && (
            <>
              <TextField
                fullWidth
                label="Name"
                margin="normal"
                value={`${selectedRequest.studentFirstName} ${selectedRequest.studentLastName}`}
                disabled
              />
              <TextField
                fullWidth
                label="Email"
                margin="normal"
                value={selectedRequest.studentEmail}
                disabled
              />
              <TextField
                fullWidth
                label="Status"
                margin="normal"
                value={selectedRequest.status}
                disabled
              />
              <TextField
                fullWidth
                label="Payment Started At"
                margin="normal"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={paymentStartedAt}
                onChange={(e) => setPaymentStartedAt(e.target.value)}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isPayed}
                    onChange={(e) => setIsPayed(e.target.checked)}
                  />
                }
                label="Payment Status"
                sx={{ mt: 2 }}
              />
              <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
                <Button variant="outlined" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApproveRequest}
                  disabled={
                    updateState.loading ||
                    (selectedRequest?.status === "Approved" &&
                      selectedRequest?.payed)
                  }
                >
                  {updateState.loading
                    ? "Updating..."
                    : "Update & Send Confirmation"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {message && (
        <MessageCard
          message={message}
          type={messageType}
          onClose={() => setMessage(null)}
        />
      )}
    </div>
  );
};

export default StudentCourseRequestsPage;
